import { ChangeDetectorRef, Component, Input, OnInit, PipeTransform } from '@angular/core';
import { ClassName } from '@app/common/enums/class-name.enum';
import { DialogService } from '@app/common/services/dialog.service';
import { LesseeEditComponent } from '@app/ps/components/lessee-edit/lessee-edit.component';
import { LesseeOption, LesseeSelection, LpisLesseeModel } from '@app/ps/models/lessee.model';
import { ProjectSubjectModel } from '@app/ps/models/project-subject.model';
import { LesseeSummaryPipe } from '@app/ps/pipes/lessee-summary.pipe';
import { LesseeService } from '@app/ps/services/lessee.service';

/**
 * Selection of lessee (pachtýř) from LPIS users or a manually created lessee.
 * The component only edits the passed selection, use LesseeService.resolveSelection to get the final lessee.
 */
@Component({
  selector: 'gmt-lessee-select',
  templateUrl: './lessee-select.component.html',
})
export class LesseeSelectComponent implements OnInit {
  @Input() selection: LesseeSelection;
  @Input() required = false; // pachtýře nelze odebrat, pouze nahradit jiným

  options: LesseeOption[];
  optionSelect: { option?: LesseeOption } = {};
  optionPipe: PipeTransform = {
    transform: (option: LesseeOption) => this.getOptionName(option),
  };

  constructor(
    private lesseeSummaryPipe: LesseeSummaryPipe,
    private lesseeService: LesseeService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef // používá se k ručnímu spuštění detekce změn po asynchronních operacích
  ) { }

  async ngOnInit() {
    const [lessees, lpisLessees] = await Promise.all([
      this.lesseeService.getAll().catch(() => [] as ProjectSubjectModel[]),
      this.lesseeService.getLpisCandidates().catch(() => [] as LpisLesseeModel[]),
    ]);
    this.options = this.createOptions(lessees || [], lpisLessees || []);
    this.cdr.markForCheck(); // spustí detekci změn po asynchronních operacích, aby se aktualizoval pohled
  }

  // backend nenabízí LPIS uživatele, ze kterých už pachtýř vznikl
  private createOptions(lessees: ProjectSubjectModel[], lpisLessees: LpisLesseeModel[]): LesseeOption[] {
    const options: LesseeOption[] = [
      ...lessees.map(lessee => ({ id: `lessee-${lessee.id}`, lessee })),
      ...lpisLessees.map(lpis => ({ id: `lpis-${lpis.id}`, lpis })),
    ];
    return options.sort((a, b) => this.optionPipe.transform(a).localeCompare(this.optionPipe.transform(b), 'cs'));
  }

  private getOptionName(option: LesseeOption): string {
    const subject = option.lessee || {
      companyName: option.lpis.obchodniJmeno,
      surname: option.lpis.prijmeni,
      firstName: option.lpis.jmeno,
      titleBefore: option.lpis.titulPredJmenem,
      degreeAfter: option.lpis.titulZaJmenem,
      identificationNumber: option.lpis.ico,
    };
    return this.lesseeSummaryPipe.transform(subject);
  }

  onOptionChanged(option?: LesseeOption) {
    if (option && option.lessee) {
      this.selection.lessee = option.lessee; // nastaví vybraného pachtýře
      this.selection.lpis = undefined; // nastaví LPIS na undefined, protože byl vybrán pachtýř
      this.optionSelect = {}; // resetuje výběr možnosti po změně pachtýře
    } else {
      this.selection.lpis = option ? option.lpis : undefined; // nastaví vybraného LPIS, pokud existuje, jinak undefined
    }
  }

  onCreateLessee() {
    const dialog = this.dialogService.open(LesseeEditComponent, {
      className: ClassName.ADJUSTED_DIALOG, // upravený dialog pro editaci pachtýře
      data: {}, // data potřebná pro vytvoření nového pachtýře
    });

    const sub = dialog.afterClosed.subscribe((created: ProjectSubjectModel | undefined) => {
      if (created) {
        this.selection.lessee = created; // nastaví nově vytvořeného pachtýře jako vybraného
        this.selection.lpis = undefined; // nastaví LPIS na undefined, protože byl vytvořen nový pachtýř
          this.optionSelect = {}; // resetuje výběr možnosti po vytvoření nového pachtýře
      }
      sub.unsubscribe();
    });
  }

  onRemoveLessee() {
    this.selection.lessee = undefined;
    this.selection.lpis = undefined;
    this.optionSelect = {};
  }
}
